// Home › Ads and calls (pages/ux-full components/marketing.js), each view drawn from its action's answer:
// how the ads did (reports.campaigns by channel), spend this month (adSpend.list), from call to invoice and
// the campaigns by return (reports.campaigns, with campaigns.list), why they called (reports.callReasons),
// today's calls by source (calls.recent), what went to Google (adConversions.list), how the ad accounts
// are doing (adAccounts.status), the ads over time (reports.campaigns by day or week), and the suggested
// ad changes (adChanges.list), one of them to decide. They draw no frame: the screen puts each in a
// section. Lists show five rows, then "Show all".
import { useState, type ReactNode } from 'react';
import type {
  AdChangeProps,
  AdChangeRow,
  AdChangesProps,
  AdsFunnelProps,
  AdsStatusProps,
  AdsSummaryProps,
  AdsTrendProps,
  CallReasonsProps,
  CallsBySourceProps,
  CampaignRow,
  CampaignTableProps,
  ConversionsProps,
  SpendProps,
} from '../schemas.ts';
import type { ViewProps } from '../host.ts';
import { clock, day, dayOf, money, perDollar, phone, plural, rate } from '../format.ts';
import { Action, Facts, Figure, Figures, Label, Meter, type LabelKind } from '../primitives.tsx';
import { adsHealthRows, adsRefusalRows } from '../rows/ads-health-rows.ts';
import { CONVERSION_WORDS } from './ads-words.ts';
import { RowList } from './RowList/RowList.tsx';

export { accountState, CONVERSION_WORDS, PLATFORM_WORDS } from './ads-words.ts';

const ROWS = 5;

/** The office's words for a campaign's channel. */
export const CHANNEL_WORDS: Record<string, string> = {
  google_ads: 'Google Ads',
  microsoft_ads: 'Microsoft Ads (Bing)',
  lsa: 'Local Services',
  web: 'Website',
  referral: 'Referral',
  vehicle: 'Vehicles',
  print: 'Print and mail',
  other: 'Other',
};
export const channelWord = (c: string | null | undefined): string =>
  c ? (CHANNEL_WORDS[c] ?? c) : 'No campaign';

/** A list's first five rows, and "Show all n" under them until it's pressed. */
export function useShown<T>(rows: readonly T[], first = ROWS): [T[], ReactNode] {
  const [all, setAll] = useState(false);
  const more =
    !all && rows.length > first ? (
      <p className="flw-more">
        <Action onClick={() => setAll(true)}>Show all {rows.length}</Action>
      </p>
    ) : null;
  return [all ? [...rows] : rows.slice(0, first), more];
}

const spent = (r: CampaignRow) =>
  r.spendCents ? (
    <>
      {money(r.spendCents)}
      {r.spendEstimated && <span className="flw-muted"> est.</span>}
    </>
  ) : (
    <span className="flw-muted">no ad cost</span>
  );

/** "Drains, search, Google Ads"; a campaign named for its channel says it once. */
export const campaignWord = (c: { name: string; channel: string }): string =>
  c.name.trim().toLowerCase() === channelWord(c.channel).toLowerCase()
    ? c.name
    : `${c.name}, ${channelWord(c.channel)}`;

/** The rows that cost something: the ads, not the referrals and the vans. */
export const paid = (rows: readonly CampaignRow[]) => rows.filter((r) => r.spendCents > 0);

/** reports.campaigns by channel for a period */
export function AdsSummary({ data: p }: ViewProps<AdsSummaryProps>) {
  const t = p.total;
  const fromAds = paid(p.rows).reduce((s, r) => s + r.calls, 0);
  const [rows, more] = useShown(
    [...p.rows].sort((a, b) => b.revenueCents - a.revenueCents || b.calls - a.calls),
    6,
  );
  return (
    <article aria-label={p.label}>
      <h3 className="flw-title">{p.label}</h3>
      <Figures
        items={[
          {
            label: 'Spent on ads',
            value: money(t.spendCents),
            ...(t.spendEstimated ? { sub: 'some of it estimated' } : {}),
          },
          { label: 'Calls', value: String(t.calls), sub: `${fromAds} from ads` },
          { label: 'Booked', value: String(t.booked), sub: `${rate(t.leadConversionRate)} of leads` },
          { label: 'Completed', value: String(t.jobsCompleted), sub: `${t.invoicedJobs} invoiced` },
          { label: 'Revenue', value: money(t.revenueCents), sub: 'invoiced, before tax' },
        ]}
      />
      {(p.note || p.adSpendNote) && (
        <p className="flw-sub">{[p.note, p.adSpendNote].filter(Boolean).join(' ')}</p>
      )}
      {!p.rows.length ? (
        <p className="flw-empty" role="status">
          No calls, leads or jobs yet.
        </p>
      ) : (
        <>
          <div className="flw-table-wrap">
            <table className="flw-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th className="flw-num">Spent</th>
                  <th className="flw-num">Calls</th>
                  <th className="flw-num">Leads</th>
                  <th className="flw-num">Booked</th>
                  <th className="flw-num">Completed</th>
                  <th className="flw-num">Revenue</th>
                  <th className="flw-num">For each $1</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key ?? 'none'}>
                    <th scope="row">{channelWord(r.channel)}</th>
                    <td className="flw-num">{spent(r)}</td>
                    <td className="flw-num">{r.calls}</td>
                    <td className="flw-num">{r.leads}</td>
                    <td className="flw-num">{r.booked}</td>
                    <td className="flw-num">{r.jobsCompleted}</td>
                    <td className="flw-num">{money(r.revenueCents)}</td>
                    <td className="flw-num">{perDollar(r.revenueCents, r.spendCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {more}
        </>
      )}
      <p className="flw-sub">
        Each job counts for the campaign it was booked under. Revenue is invoiced, before tax, less credit
        notes.
      </p>
    </article>
  );
}

/** A bar's row: its label, the drawn bar, the figure, and the share kept under the label. */
function Bar({
  label,
  value,
  max,
  figure,
  sub,
}: {
  label: string;
  value: number;
  max: number;
  figure: ReactNode;
  sub?: string | null;
}) {
  return (
    <li className="flw-bar">
      <span>{label}</span>
      <Meter value={value} max={max} label={label} />
      <b>{figure}</b>
      {sub && <span className="flw-sub">{sub}</span>}
    </li>
  );
}

/** reports.campaigns' paid rows, from call to invoice */
export function AdsFunnel({ data: p }: ViewProps<AdsFunnelProps>) {
  const rows = paid(p.rows);
  const s = (k: 'calls' | 'leads' | 'booked' | 'jobsCompleted' | 'invoicedJobs') =>
    rows.reduce((a, r) => a + r[k], 0);
  const steps: [string, number][] = [
    ['Calls', s('calls')],
    ['Leads', s('leads')],
    ['Booked', s('booked')],
    ['Completed', s('jobsCompleted')],
    ['Invoiced', s('invoicedJobs')],
  ];
  if (!steps[0]![1])
    return (
      <p className="flw-empty" role="status">
        No calls from the ads yet.
      </p>
    );
  return (
    <article aria-label={p.label}>
      <p className="flw-sub">{p.label}</p>
      <ul className="flw-rows">
        {steps.map(([label, n], i) => {
          const before = i ? steps[i - 1]! : null;
          return (
            <Bar
              key={label}
              label={label}
              value={n}
              max={steps[0]![1]}
              figure={n}
              sub={
                before && before[1]
                  ? `${Math.round((n / before[1]) * 100)} % of ${before[0].toLowerCase()}`
                  : null
              }
            />
          );
        })}
      </ul>
    </article>
  );
}

const days = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;

/** adSpend.list: the month's spend by platform, what's estimated, what's on no campaign yet */
export function SpendThisMonth({ data: p }: ViewProps<SpendProps>) {
  if (!p.entries.length)
    return (
      <p className="flw-empty" role="status">
        No ad spend recorded this month.
      </p>
    );
  // each entry's share up to today, spread evenly over its days as the report spreads it
  const upTo = p.today < p.to ? p.today : p.to;
  const share = (e: SpendProps['entries'][number]) => {
    const end = upTo < e.to ? upTo : e.to;
    return end < e.from ? 0 : Math.round((e.amountCents * days(e.from, end)) / days(e.from, e.to));
  };
  const total = p.entries.reduce((a, e) => a + share(e), 0);
  const gone = days(p.from, upTo);
  const month = days(p.from, p.to);
  const byPlatform = new Map<string, { cents: number; estimated: boolean }>();
  for (const e of p.entries) {
    const b = byPlatform.get(e.channel) ?? { cents: 0, estimated: false };
    byPlatform.set(e.channel, { cents: b.cents + share(e), estimated: b.estimated || e.estimated });
  }
  const platforms = [...byPlatform].sort((a, b) => b[1].cents - a[1].cents);
  const most = Math.max(1, ...platforms.map(([, b]) => b.cents));
  const unlinked = p.entries.filter((e) => !e.campaign);
  return (
    <article aria-label="Spend this month">
      <Figure label="Spent on ads this month" value={money(total)} sub={`day ${gone} of ${month}`} />
      <ul className="flw-rows">
        {platforms.map(([ch, b]) => (
          <Bar
            key={ch}
            label={channelWord(ch)}
            value={b.cents}
            max={most}
            figure={money(b.cents)}
            sub={b.estimated ? 'estimated: an invoice spread over its days' : null}
          />
        ))}
      </ul>
      <p className="flw-sub">
        At this rate the month ends near <b>{money(Math.round((total / gone) * month))}</b>.
      </p>
      {unlinked.length > 0 && (
        <p className="flw-sub">
          {money(unlinked.reduce((a, e) => a + share(e), 0))} is on {plural(unlinked.length, 'ad campaign')}{' '}
          not linked to a campaign yet:{' '}
          {unlinked.map((e) => e.platformCampaign?.name ?? channelWord(e.channel)).join(', ')}.
        </p>
      )}
    </article>
  );
}

/** reports.campaigns by campaign, best return first, beside campaigns.list */
export function CampaignTable({ data: p }: ViewProps<CampaignTableProps>) {
  // gross profit only for whoever the server gave it to (finance.margins.view)
  const margins = 'grossProfitCents' in p.total;
  const ranked = [...p.rows].sort(
    (a, b) => (b.roas ?? -1) - (a.roas ?? -1) || b.revenueCents - a.revenueCents || b.calls - a.calls,
  );
  const [rows, more] = useShown(ranked);
  const ads = paid(p.rows);
  const sum = (k: 'spendCents' | 'revenueCents') => ads.reduce((a, r) => a + r[k], 0);
  const gp = ads.reduce((a, r) => a + (r.grossProfitCents ?? 0), 0);
  if (!p.rows.length)
    return (
      <p className="flw-empty" role="status">
        No calls, leads or jobs on a campaign yet.
      </p>
    );
  const info = (key: string | null) => p.campaigns.find((c) => c.id === key);
  return (
    <article aria-label={p.label}>
      <p className="flw-sub">
        {p.label}
        {sum('spendCents') > 0 &&
          `. The paid ones together: ${money(sum('spendCents'))} brought ${money(sum('revenueCents'))}, ${perDollar(sum('revenueCents'), sum('spendCents'))} for each $1${margins ? `, ${perDollar(gp, sum('spendCents'))} of it gross profit` : ''}.`}
      </p>
      <div className="flw-table-wrap">
        <table className="flw-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th className="flw-num">Spent</th>
              <th className="flw-num">Calls</th>
              <th className="flw-num">Booked</th>
              <th className="flw-num">Cost a booking</th>
              <th className="flw-num">Completed</th>
              <th className="flw-num">Revenue</th>
              <th className="flw-num">For each $1</th>
              {margins && <th className="flw-num">Gross profit for each $1</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const c = info(r.key);
              const sub = [
                r.key ? channelWord(r.channel) : 'Calls on no tracking number',
                ...(c?.numbers.map((n) => n.text) ?? []),
                ...(c && !c.active ? ['retired'] : []),
              ];
              return (
                <tr key={r.key ?? 'none'}>
                  <th scope="row">
                    {r.key ? r.label : 'No campaign'}
                    <span className="flw-sub">{sub.join(', ')}</span>
                  </th>
                  <td className="flw-num">{spent(r)}</td>
                  <td className="flw-num">{r.calls}</td>
                  <td className="flw-num">{r.booked}</td>
                  <td className="flw-num">
                    {r.costPerBookedJobCents === null ? '—' : money(r.costPerBookedJobCents)}
                  </td>
                  <td className="flw-num">{r.jobsCompleted}</td>
                  <td className="flw-num">{money(r.revenueCents)}</td>
                  <td className="flw-num">{perDollar(r.revenueCents, r.spendCents)}</td>
                  {margins && (
                    <td className="flw-num">
                      {r.grossProfitCents == null ? '—' : perDollar(r.grossProfitCents, r.spendCents)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {more}
      {(p.adSpendNote || p.grossProfitNote) && (
        <p className="flw-sub">{[p.adSpendNote, p.grossProfitNote].filter(Boolean).join(' ')}</p>
      )}
    </article>
  );
}

/** reports.callReasons: calls taken by reason, with the total and the missed ones */
export function CallReasons({ data: p }: ViewProps<CallReasonsProps>) {
  const [rows, more] = useShown([...p.rows].sort((a, b) => b.taken - a.taken));
  if (!p.rows.length)
    return (
      <p className="flw-empty" role="status">
        No calls taken yet.
      </p>
    );
  const cells = (r: CallReasonsProps['total']) => (
    <>
      <td className="flw-num">{r.taken}</td>
      <td className="flw-num">{r.leads}</td>
      <td className="flw-num">{r.booked}</td>
      <td className="flw-num">{r.unbookedLeads}</td>
      <td className="flw-num">{r.excused}</td>
      <td className={`flw-num${r.notSet ? ' flw-bad' : ''}`}>{r.notSet}</td>
      <td className="flw-num">{rate(r.rate)}</td>
    </>
  );
  return (
    <article aria-label={p.label}>
      <p className="flw-sub">
        {p.label}, by the reason the office set. {plural(p.missed, 'more was', 'more were')} missed.
      </p>
      <div className="flw-table-wrap">
        <table className="flw-table">
          <thead>
            <tr>
              <th>Reason</th>
              <th className="flw-num">Calls</th>
              <th className="flw-num">Leads</th>
              <th className="flw-num">Booked</th>
              <th className="flw-num">Leads not booked</th>
              <th className="flw-num">Excused</th>
              <th className="flw-num">No reason yet</th>
              <th className="flw-num">Booking rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.key ?? 'none'}-${r.label}`}>
                <th scope="row">{r.label}</th>
                {cells(r)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">All calls taken</th>
              {cells(p.total)}
            </tr>
          </tfoot>
        </table>
      </div>
      {more}
    </article>
  );
}

type Call = CallsBySourceProps['calls'][number];
/** What came of a call, as a word: booked or on the line in ink, a missed call not texted back red. */
export function callOutcome(c: Call): [string, LabelKind] {
  if (c.outcome === 'booked') return ['Booked', 'now'];
  if (c.status === 'missed' || c.status === 'voicemail')
    return [c.status === 'voicemail' ? 'Voicemail' : 'Missed', c.textedBackAt ? 'muted' : 'bad'];
  if (c.status === 'in_progress') return ['On the line', 'now'];
  if (c.outcome === 'not_booked') return ['Not booked', 'muted'];
  return ['No outcome yet', 'wait'];
}

/** calls.recent: each call with the campaign its number is on, its reason and what came of it */
export function CallsBySource({ data: p, host }: ViewProps<CallsBySourceProps>) {
  const [rows, more] = useShown(p.calls);
  if (!p.calls.length)
    return (
      <p className="flw-empty" role="status">
        No calls yet today.
      </p>
    );
  return (
    <article aria-label={p.label}>
      <p className="flw-sub">
        {p.label}, {plural(p.calls.length, 'call')}
      </p>
      <ul className="flw-rows">
        {rows.map((c) => {
          const [word, kind] = callOutcome(c);
          const who =
            c.matches.find((m) => m.customerId === c.customerId)?.customerName ?? c.matches[0]?.customerName;
          const customer = c.customerId ?? c.matches[0]?.customerId;
          return (
            <li key={c.id} className="flw-row flw-call">
              <span className={kind === 'bad' ? 'flw-bad' : 'flw-muted'}>
                {clock(c.startedAt, p.timeZone)}
              </span>
              <span>
                {who && customer ? (
                  <Action onClick={() => host.open({ kind: 'customer', id: customer })}>{who}</Action>
                ) : (
                  <b>{who ?? phone(c.from)}</b>
                )}
                <span className="flw-sub">
                  {c.campaign ? campaignWord(c.campaign) : 'No campaign'}
                  {who ? `. ${phone(c.from)}` : ''}
                  {c.trackingLabel ? `, rang ${c.trackingLabel}` : ''}
                </span>
              </span>
              <span>
                <Label kind="muted">Reason</Label>
                <span className={`flw-cell${c.reason ? '' : ' flw-muted'}`}>
                  {c.reason?.name ??
                    (c.outcome === 'booked' || c.status === 'in_progress' ? '—' : 'Not set yet')}
                </span>
              </span>
              <span>
                <Label kind={kind}>{word}</Label>
                <span className="flw-sub">
                  {c.textedBackAt
                    ? `Texted back at ${clock(c.textedBackAt, p.timeZone)}`
                    : c.takenBy
                      ? `Taken by ${c.takenBy}`
                      : ''}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {more}
    </article>
  );
}

type Conversion = ConversionsProps['conversions'][number];
export const CONVERSION: Record<Conversion['status'], [string, LabelKind]> = {
  sent: ['Sent', 'muted'],
  queued: ['In the queue', 'wait'],
  refused: ['Refused', 'bad'],
  skipped: ['Kept back', 'muted'],
};
/** Each kind of conversion in the office's words: what Google is told happened. */
/** What Google holds for a sent one, when it moved after sending: taken back, or a new value. */
export function since(c: Conversion, tz: string): string {
  if (c.retractedAt) return `. Taken back ${dayOf(c.retractedAt, tz)}`;
  if (c.status === 'sent' && c.sentValueCents !== null && c.sentValueCents !== c.valueCents)
    return `. Now ${money(c.sentValueCents)}`;
  return '';
}

/** adConversions.list: each one sent to Google Ads, or waiting or kept back, and Google's answer */
export function Conversions({
  data: p,
  host,
  onMore,
}: ViewProps<ConversionsProps> & {
  /** The next page, when there is one (adConversions.list's next). */
  onMore?: () => void;
}) {
  const [rows, more] = useShown(p.conversions);
  if (!p.conversions.length)
    return (
      <p className="flw-empty" role="status">
        Nothing has been sent to Google yet.
      </p>
    );
  return (
    <article aria-label="Sent to Google">
      <p className="flw-sub">
        What came from a Google ad, each under its own conversion action: a lead call, a job’s first quote, a
        job booked and completed with its invoices before tax. Each job goes to an action once; one AdTrack
        already sent is kept back.
        {p.total !== undefined && p.total > p.conversions.length ? ` ${p.total} in all.` : ''}
      </p>
      <ul className="flw-rows">
        {rows.map((c) => (
          <li key={c.id} className="flw-row">
            <span>
              {c.job ? (
                <Action onClick={() => host.open({ kind: 'job', id: c.job!.id })}>{c.job.number}</Action>
              ) : (
                <b>Lead call</b>
              )}
              <span className="flw-sub">
                {c.job ? `${CONVERSION_WORDS[c.kind]}, from` : 'From'} a Google {c.method}
                {c.valueCents !== null ? `, ${money(c.valueCents)}` : ''}
                {c.sentAt
                  ? `. Sent ${dayOf(c.sentAt, p.timeZone)}, ${clock(c.sentAt, p.timeZone)}`
                  : c.status === 'refused'
                    ? `. Tried ${plural(c.attempts, 'time')}`
                    : ''}
                {since(c, p.timeZone)}
              </span>
              {c.lastError && (
                <span className={`flw-sub${c.status === 'refused' ? ' flw-bad' : ''}`}>{c.lastError}</span>
              )}
            </span>
            <Label kind={CONVERSION[c.status][1]}>{CONVERSION[c.status][0]}</Label>
          </li>
        ))}
      </ul>
      {more ??
        (onMore && (
          <p className="flw-more">
            <Action onClick={onMore}>Show more</Action>
          </p>
        ))}
    </article>
  );
}

/**
 * adAccounts.status on RowList (rows/ads-health-rows.ts): each problem first, a row with its words in red, then
 * each ad account and its last pull and what went to Google this week; Google's latest refusals under them.
 */
export function AdsHealth({ data: p }: ViewProps<AdsStatusProps>) {
  const refusals = adsRefusalRows(p);
  return (
    <article aria-label="How the ad accounts are doing">
      <RowList label="How the ad accounts are doing" rows={adsHealthRows(p)} />
      {refusals.length > 0 && <RowList title="Google’s latest refusals" rows={refusals} />}
    </article>
  );
}

/** A period's row label: the day, or the week from its Monday, without the year (the range says it). */
export const periodWord = (unit: 'day' | 'week', key: string | null | undefined, label: string) => {
  if (!key) return label;
  const d = day(key).replace(/ \d{4}$/, '');
  return unit === 'week' ? `Week of ${d}` : d;
};

/** reports.campaigns by day or week: each period's calls, repeat calls, bookings, revenue and spend */
export function AdsTrend({ data: p }: ViewProps<AdsTrendProps>) {
  const [rows, more] = useShown([...p.rows].reverse(), 7);
  if (!p.rows.some((r) => r.calls || r.leads || r.revenueCents || r.spendCents))
    return (
      <p className="flw-empty" role="status">
        No calls, leads, jobs or spend in these days.
      </p>
    );
  return (
    <article aria-label={p.label}>
      <p className="flw-sub">{p.label}, newest first</p>
      <div className="flw-table-wrap">
        <table className="flw-table">
          <thead>
            <tr>
              <th>{p.unit === 'week' ? 'Week' : 'Day'}</th>
              <th className="flw-num">Calls</th>
              <th className="flw-num">Of them repeat</th>
              <th className="flw-num">Leads</th>
              <th className="flw-num">Booked</th>
              <th className="flw-num">Revenue</th>
              <th className="flw-num">Spent</th>
              <th className="flw-num">Cost a lead</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.period ?? r.label}>
                <th scope="row">{periodWord(p.unit, r.period, r.label)}</th>
                <td className="flw-num">{r.calls}</td>
                <td className="flw-num">{r.repeatCalls ?? '—'}</td>
                <td className="flw-num">{r.leads}</td>
                <td className="flw-num">{r.booked}</td>
                <td className="flw-num">{money(r.revenueCents)}</td>
                <td className="flw-num">{spent(r)}</td>
                <td className="flw-num">{r.costPerLeadCents === null ? '—' : money(r.costPerLeadCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">All of it</th>
              <td className="flw-num">{p.total.calls}</td>
              <td className="flw-num">{p.total.repeatCalls ?? '—'}</td>
              <td className="flw-num">{p.total.leads}</td>
              <td className="flw-num">{p.total.booked}</td>
              <td className="flw-num">{money(p.total.revenueCents)}</td>
              <td className="flw-num">{spent(p.total)}</td>
              <td className="flw-num">
                {p.total.costPerLeadCents === null ? '—' : money(p.total.costPerLeadCents)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {more}
      {p.adSpendNote && <p className="flw-sub">{p.adSpendNote}</p>}
    </article>
  );
}

/** Where a suggested ad change stands, as a word: waiting dashed, to do in ink, the rest muted. */
export function changeState(c: Pick<AdChangeRow, 'status' | 'platform'>): [string, LabelKind] {
  const where = channelWord(c.platform);
  if (c.status === 'waiting') return ['Waiting for a decision', 'wait'];
  if (c.status === 'approved') return [`To do in ${where}`, 'now'];
  return [c.status === 'done' ? `Done in ${where}` : 'Dismissed', 'muted'];
}

export const at = (iso: string, timeZone: string) => `${dayOf(iso, timeZone)}, ${clock(iso, timeZone)}`;

/** A decision on a suggested ad change: approve it, or dismiss it saying why not. */
export interface AdChangeDecision {
  decision: 'approve' | 'dismiss';
  /** Why not: a dismissal needs one, and it's kept for the next suggestions. */
  note?: string;
}

/**
 * One suggested ad change (adChanges.list): where, what and why with its figures, who prepared it, and
 * where it stands. Deciding and marking done are the screen's callbacks, drawn only when it passes them;
 * a chat never decides here. Flowology changes nothing in the ad account: an approved change is one to
 * make there by hand, then mark done.
 */
export function AdChange({
  data: p,
  host,
  onDecide,
  onDone,
  busy = false,
  cannotDecide = null,
}: ViewProps<AdChangeProps> & {
  /** The office decides here (adChanges.decide). */
  onDecide?: (d: AdChangeDecision) => void;
  /** Marks an approved change done once it's made in the platform (adChanges.markDone). */
  onDone?: () => void;
  /** A decision is on its way: nothing can be pressed twice. */
  busy?: boolean;
  /** Why this person can't decide it, in plain words; null when they can. */
  cannotDecide?: string | null;
}) {
  const c = p.change;
  const chat = host.surface === 'chat';
  const [dismissing, setDismissing] = useState(false);
  const [why, setWhy] = useState('');
  const where = channelWord(c.platform);
  const [word, kind] = changeState(c);
  const d = c.decided;
  return (
    <article className="flw-req flw-adc" aria-label={c.title}>
      <p className="flw-req-state">
        <Label kind={kind}>{word}</Label>
      </p>
      <p className="flw-sub">
        {where}, {c.target}
      </p>
      <h3 className="flw-title">{c.title}</h3>
      <Facts
        rows={[
          ['Why', c.why],
          ['What changes', c.effect],
          ['Prepared by', `${c.preparedBy.name}, ${at(c.preparedAt, p.timeZone)}`],
        ]}
      />
      {c.figures.length > 0 && (
        <Figures
          items={c.figures.slice(0, 4).map((f) => ({
            label: f.label,
            value: f.unit === 'money' ? money(f.value) : String(f.value),
          }))}
        />
      )}
      {d && (
        <p className={`flw-said${c.status === 'dismissed' ? ' flw-said-off' : ''}`} role="status">
          <b>
            {c.status === 'dismissed' ? 'Dismissed' : 'Approved'} by {d.by},{' '}
            {/* a time ends in "a.m.": its own dot ends the sentence */}
            {at(d.at, p.timeZone).replace(/\.$/, '')}.
          </b>
          {d.note && (
            <span className="flw-sub">
              {c.status === 'dismissed' ? 'Why not' : 'Note'}: “{d.note}”
            </span>
          )}
          {c.status === 'approved' && (
            <span className="flw-sub">
              Make it in {where} by hand: Flowology doesn’t change the ad account.
            </span>
          )}
          {c.done && (
            <span className="flw-sub">
              Made in {where}: {c.done.by} marked it done, {at(c.done.at, p.timeZone)}
            </span>
          )}
        </p>
      )}
      {c.status === 'waiting' &&
        (chat || !onDecide ? (
          <p className="flw-waits">Decided in Flowology, on How the ads did.</p>
        ) : cannotDecide ? (
          <p className="flw-waits">{cannotDecide}</p>
        ) : dismissing ? (
          <div className="flw-req-decline">
            <label className="flw-fld">
              <span>Why not? It’s kept, so the review doesn’t suggest it again soon</span>
              <input
                className="flw-field"
                value={why}
                maxLength={500}
                onChange={(e) => setWhy(e.target.value)}
              />
            </label>
            <div className="flw-acts">
              <button
                type="button"
                className="flw-btn flw-btn-pri"
                disabled={busy || !why.trim()}
                onClick={() => onDecide({ decision: 'dismiss', note: why.trim() })}
              >
                {busy ? 'Dismissing…' : 'Dismiss it'}
              </button>
              <button type="button" className="flw-link" disabled={busy} onClick={() => setDismissing(false)}>
                Keep it waiting
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flw-acts">
              <button
                type="button"
                className="flw-btn flw-btn-pri"
                disabled={busy}
                onClick={() => onDecide({ decision: 'approve' })}
              >
                {busy ? 'Approving…' : `Approve: to do in ${where}`}
              </button>
              <button type="button" className="flw-link" disabled={busy} onClick={() => setDismissing(true)}>
                Dismiss…
              </button>
            </div>
            <p className="flw-sub">
              Approving puts it on the list to make in {where} by hand. Flowology doesn’t change the ad
              account.
            </p>
          </>
        ))}
      {c.status === 'approved' && onDone && !chat && (
        <div className="flw-acts">
          <button type="button" className="flw-btn flw-btn-pri" disabled={busy} onClick={onDone}>
            {busy ? 'Marking…' : `Mark done: made in ${where}`}
          </button>
        </div>
      )}
    </article>
  );
}

/**
 * adChanges.list: the queue, one row a change: what, where, who prepared it or decided it, and where it
 * stands. Its one action is the title, which opens the change (AdChange) when the screen passes onOpen.
 */
export function AdChangeList({
  data: p,
  onOpen,
  empty,
}: ViewProps<AdChangesProps> & {
  /** Opens one change to decide or mark done: the screen's. */
  onOpen?: (id: string) => void;
  /** The words with nothing to list. */
  empty?: string;
}) {
  const [rows, more] = useShown(p.changes);
  if (!p.changes.length)
    return (
      <p className="flw-empty" role="status">
        {empty ??
          'Nothing waits. The weekly ads review adds what it finds here each Monday; so can a person, or an AI tool they’ve connected. Each waits for someone here to decide it.'}
      </p>
    );
  return (
    <>
      <ul className="flw-rows">
        {rows.map((c) => {
          const [word, kind] = changeState(c);
          const d = c.decided;
          return (
            <li key={c.id} className="flw-row">
              <span>
                {onOpen ? (
                  <button type="button" className="flw-open" onClick={() => onOpen(c.id)}>
                    {c.title}
                  </button>
                ) : (
                  <b className="flw-open">{c.title}</b>
                )}
                <span className="flw-sub">
                  {channelWord(c.platform)}, {c.target}.{' '}
                  {d
                    ? `${c.status === 'dismissed' ? 'Dismissed' : 'Approved'} by ${d.by}, ${dayOf(d.at, p.timeZone)}`
                    : `Prepared by ${c.preparedBy.name}, ${dayOf(c.preparedAt, p.timeZone)}`}
                </span>
              </span>
              <Label kind={kind}>{word}</Label>
            </li>
          );
        })}
      </ul>
      {more}
    </>
  );
}
